import { useState, useEffect } from 'react';
import ApiService from '../services/api';

export const useDataLoader = () => {
  const [data, setData] = useState({
    clients: [],
    invoices: [],
    quotes: [],
    documents: [], // Added documents
    settings: null,
    userPermissions: [],
    loading: true,
    error: null
  });

  // Load all data from API
  const loadData = async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));
      
      // allSettled, not all: a single failing endpoint used to reject the whole
      // batch, leaving the dashboard empty even though the other calls succeeded.
      const results = await Promise.allSettled([
        ApiService.getClients(),
        ApiService.getInvoices(),
        ApiService.getQuotes(),
        ApiService.getSettings(),
        ApiService.getUserPermissions()
      ]);

      const [clientsRes, invoicesRes, quotesRes, settingsRes, permissionsRes] = results;
      const valueOf = (result, fallback) =>
        result.status === 'fulfilled' ? (result.value?.data ?? fallback) : fallback;

      const failed = [];
      const labels = ['clients', 'invoices', 'quotes', 'settings', 'permissions'];
      results.forEach((result, i) => {
        if (result.status === 'rejected') {
          console.error(`Failed to load ${labels[i]}:`, result.reason);
          failed.push(labels[i]);
        }
      });

      setData({
        clients: valueOf(clientsRes, []),
        invoices: valueOf(invoicesRes, []),
        quotes: valueOf(quotesRes, []),
        documents: [], // Initialize empty, will be loaded per client
        settings: valueOf(settingsRes, null),
        userPermissions: valueOf(permissionsRes, []),
        loading: false,
        error: failed.length ? `Could not load: ${failed.join(', ')}` : null
      });
    } catch (error) {
      console.error('Error loading data:', error);
      setData(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load data'
      }));
    }
  };

  // Load documents for a specific client
  const loadClientDocuments = async (clientId) => {
    try {
      const response = await ApiService.getClientDocuments(clientId);
      if (response.success) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Failed to load client documents:', error);
      throw error;
    }
  };

  // Client operations
  const saveClient = async (clientData, isEdit = false) => {
    try {
      if (isEdit) {
        await ApiService.updateClient(clientData._id, clientData);
      } else {
        await ApiService.createClient(clientData);
      }
      await loadData(); // Refresh data
    } catch (error) {
      throw error;
    }
  };

  const deleteClient = async (id) => {
    try {
      await ApiService.deleteClient(id);
      await loadData(); // Refresh data
    } catch (error) {
      throw error;
    }
  };

  // Document operations
  const uploadDocument = async (clientId, file, documentInfo) => {
    try {
      const response = await ApiService.uploadDocument(clientId, file, documentInfo);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Upload failed');
    } catch (error) {
      throw error;
    }
  };

  const deleteDocument = async (documentId) => {
    try {
      const response = await ApiService.deleteDocument(documentId);
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Delete failed');
    } catch (error) {
      throw error;
    }
  };

  const updateDocument = async (documentId, updates) => {
    try {
      const response = await ApiService.updateDocument(documentId, updates);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Update failed');
    } catch (error) {
      throw error;
    }
  };

  const downloadDocument = (documentId) => {
    try {
      ApiService.downloadDocument(documentId);
    } catch (error) {
      console.error('Failed to download document:', error);
      throw error;
    }
  };

  // Invoice operations
  const saveInvoice = async (invoiceData, isEdit = false) => {
    try {
      if (isEdit) {
        await ApiService.updateInvoice(invoiceData._id, invoiceData);
      } else {
        await ApiService.createInvoice(invoiceData);
      }
      await loadData(); // Refresh data
    } catch (error) {
      throw error;
    }
  };

  const deleteInvoice = async (id) => {
    try {
      await ApiService.deleteInvoice(id);
      await loadData(); // Refresh data
    } catch (error) {
      throw error;
    }
  };

  // Quote operations
  const saveQuote = async (quoteData, isEdit = false) => {
    try {
      if (isEdit) {
        await ApiService.updateQuote(quoteData.id, quoteData);
      } else {
        await ApiService.createQuote(quoteData);
      }
      await loadData(); // Refresh data
    } catch (error) {
      throw error;
    }
  };

  const deleteQuote = async (id) => {
    try {
      await ApiService.deleteQuote(id);
      await loadData(); // Refresh data
    } catch (error) {
      throw error;
    }
  };

  // Payment operations
  const recordPayment = async (paymentData) => {
    try {
      await ApiService.recordPayment(paymentData);
      await loadData(); // Refresh data to update invoice status
    } catch (error) {
      throw error;
    }
  };

  const deletePayment = async (id) => {
    try {
      await ApiService.deletePayment(id);
      await loadData(); // Refresh data
    } catch (error) {
      throw error;
    }
  };

  const updatePayment = async (id, paymentData) => {
    try {
      await ApiService.updatePayment(id, paymentData);
      await loadData(); // Refresh data to update invoice status
    } catch (error) {
      throw error;
    }
  };

  // Settings operations - ENHANCED WITH DEBUG LOGGING
  const saveSettings = async (settingsData) => {
    try {
      // console.log('💾 useDataLoader: saveSettings called with:', settingsData);
      
      const response = await ApiService.updateSettings(settingsData);
      // console.log('💾 useDataLoader: API response:', response);
      
      // console.log('💾 useDataLoader: Reloading data after settings save...');
      await loadData(); // Refresh data
      
      // console.log('💾 useDataLoader: Settings save complete');
    } catch (error) {
      console.error('💾 useDataLoader: Settings save error:', error);
      throw error;
    }
  };

  // Convert quote to invoice
  // Project operations


  // Task operations


  const convertQuoteToInvoice = async (quote) => {
    try {
      const invoiceData = {
        clientId: quote.clientId,
        items: quote.items,
        discountPercentage: quote.discountPercentage,
        notes: quote.notes,
        status: 'draft',
        date: new Date().toISOString().split('T')[0],
      };
      
      await ApiService.createInvoice(invoiceData);
      await ApiService.updateQuote(quote._id, { ...quote, status: 'converted' });
      await loadData(); // Refresh data
    } catch (error) {
      throw error;
    }
  };

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  return {
    ...data,
    loadData,
    saveClient,
    deleteClient,
    saveInvoice,
    deleteInvoice,
    saveQuote,
    deleteQuote,
    recordPayment,
    deletePayment,
    updatePayment,
    saveSettings,
    convertQuoteToInvoice,
    // Document operations
    loadClientDocuments,
    uploadDocument,
    deleteDocument,
    updateDocument,
    downloadDocument
  };
};